import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  Grid,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Snackbar,
  Toolbar,
} from "@mui/material";
import { Item } from "../types/Item";
import auctionService from "../services/auctionService";
import { useAuth } from "../contexts/AuthContext";
import { formatPrice } from "../utils/formatters";

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pendingItems, setPendingItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewItem, setViewItem] = useState<Item | null>(null);
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    if (user && user.role !== "admin" && user.role !== "blog") {
      navigate("/");
      return;
    }
    // Blog editors get a landing page; pending-auction tools are admin-only.
    if (user?.role === "blog") {
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const pending = await auctionService.getPendingAuctions();
        setPendingItems(pending);
        setLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load data");
        setLoading(false);
      }
    };
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [user, navigate]);

  const handleApprove = async (itemId: string) => {
    setProcessing(true);
    try {
      await auctionService.approveItem(itemId);
      setSuccess("Auction approved successfully.");
      setPendingItems(pendingItems.filter((item) => item.id !== itemId));
      const pending = await auctionService.getPendingAuctions();
      setPendingItems(pending);
    } catch (err: any) {
      if (err?.response?.data?.detail?.includes("not pending")) {
        setError("This auction has already been processed.");
        const pending = await auctionService.getPendingAuctions();
        setPendingItems(pending);
      } else {
        setError(
          err instanceof Error ? err.message : "Failed to approve auction"
        );
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedItem || !rejectionReason.trim()) {
      setError("Please provide a reason for rejection");
      return;
    }
    setProcessing(true);
    try {
      await auctionService.rejectItem(selectedItem.id);
      setSuccess("Auction rejected successfully.");
      setPendingItems(
        pendingItems.filter((item) => item.id !== selectedItem.id)
      );
      setRejectDialogOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reject auction");
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedItem) return;
    setProcessing(true);
    try {
      await auctionService.deleteItem(selectedItem.id);
      setSuccess("Auction deleted successfully.");
      setPendingItems(
        pendingItems.filter((item) => item.id !== selectedItem.id)
      );
      setDeleteDialogOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete auction");
    } finally {
      setProcessing(false);
    }
  };

  const openRejectDialog = (item: Item) => {
    setSelectedItem(item);
    setRejectDialogOpen(true);
  };

  const openDeleteDialog = (item: Item) => {
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  };

  const handleView = (item: Item) => {
    setViewItem(item);
    setViewDialogOpen(true);
  };

  const trulyPendingItems = pendingItems.filter(
    (item) => item.approval_status === "pending"
  );

  if (user?.role === "blog") {
    return (
      <Box sx={{ width: "100%" }}>
        <Box component="main" sx={{ p: 3 }}>
          <Toolbar />
          <Typography variant="h4" gutterBottom>
            Dashboard
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            You are signed in as a blog editor. Use Blog Management to create and
            edit posts.
          </Alert>
          <Button
            variant="contained"
            onClick={() => navigate("/admin/blogs")}
          >
            Open Blog Management
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box component="main" sx={{ p: 3 }}>
        <Toolbar />
        <Typography variant="h4" gutterBottom>
          Admin Dashboard
        </Typography>
        {loading && <CircularProgress />}
        {error && <Alert severity="error">{error}</Alert>}
        <Snackbar
          open={!!success}
          autoHideDuration={6000}
          onClose={() => setSuccess(null)}
          message={success}
        />
        <Grid container spacing={2}>
          {trulyPendingItems.length > 0 ? (
            trulyPendingItems.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <Card>
                  <CardMedia
                    component="img"
                    height="140"
                    image={
                      Array.isArray(item.images) && item.images.length > 0
                        ? item.images[0]
                        : "/default-auction.png"
                    }
                    alt={item.title}
                    onClick={() => handleView(item)}
                    sx={{ cursor: "pointer" }}
                  />
                  <CardContent>
                    <Typography gutterBottom variant="h6" component="div">
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.description}
                    </Typography>
                    <Box mt={2} display="flex" justifyContent="flex-end">
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => handleApprove(item.id)}
                        disabled={processing}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => openRejectDialog(item)}
                        disabled={processing}
                      >
                        Reject
                      </Button>
                      <Button
                        variant="outlined"
                        color="secondary"
                        size="small"
                        sx={{ ml: 1 }}
                        onClick={() => openDeleteDialog(item)}
                        disabled={processing}
                      >
                        Delete
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid item xs={12}>
              <Typography>No pending auctions.</Typography>
            </Grid>
          )}
        </Grid>
      </Box>

      <Dialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
      >
        <DialogTitle>Reject Auction</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Reason for Rejection"
            type="text"
            fullWidth
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleReject} color="error" disabled={processing}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to permanently delete this auction? This
            action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" disabled={processing}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{viewItem?.title}</DialogTitle>
        <DialogContent>
          {viewItem && (
            <Box>
              <Typography variant="h6">Description</Typography>
              <Typography paragraph>{viewItem.description}</Typography>
              <Typography variant="h6">Starting Price</Typography>
              <Typography paragraph>
                {formatPrice(viewItem.starting_price)}
              </Typography>
              <Typography variant="h6">End Time</Typography>
              <Typography paragraph>
                {viewItem.end_time
                  ? new Date(viewItem.end_time).toLocaleString()
                  : "Not set"}
              </Typography>
              <Typography variant="h6">Images</Typography>
              <Grid container spacing={1}>
                {Array.isArray(viewItem.images) &&
                  viewItem.images.map((img, index) => (
                    <Grid item xs={4} key={index}>
                      <img
                        src={img}
                        alt={`Auction item ${index + 1}`}
                        style={{ width: "100%", height: "auto" }}
                      />
                    </Grid>
                  ))}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDashboard;
